import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { useInternationalization } from '@progress/kendo-react-intl';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import {
  SyntheticEvent,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import AddToCartIcon from 'src/assets/icons/AddToCartIcon';
import NoItemIcon from 'src/assets/icons/NoItemIcon';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import NumberStepper from 'src/components/design-system/NumberStepper';
import PopUp from 'src/components/design-system/PopUp';
import useGuardedAsyncCallback from 'src/hooks/useGuardedAsyncCallback';
import ProductAttributeIcons from 'src/sections/catalog_products/ProductAttributeIcons';
import { ViewCatalogProductsContext } from 'src/sections/catalog_products/ViewCatalogProductsContext';
import { Product } from 'src/types/catalog';
import { useContextSelector } from 'use-context-selector';
import { ProductImage } from '../ProductImage';

export interface PictureViewCardProps {
  selected: boolean;
  onSelect: () => void;
  product: Product & { attributes: string[] };
  onQuantityChange?: (qty: number) => void;
  quantity?: number;
}

export const PictureViewCard: FC<PictureViewCardProps> = ({
  selected,
  onSelect,
  product,
  onQuantityChange,
  quantity: propQuantity,
}: PictureViewCardProps): ReactElement<HTMLElement> => {
  const { t } = useTranslation();
  const intl = useInternationalization();
  const selectedCatalog = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.selectedCatalog
  );
  const isExternalCatalog = selectedCatalog?.catalogType === 2;
  const isOfflineSupplier = selectedCatalog?.catalogType === 3;

  const { description, price, uom, itemNum, brandName, companyName } = product;

  const handleViewProductDetails = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.handleViewProductDetails
  );
  const isFetchingProductDetails = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.isFetchingProductDetails
  );
  const loadingProductDetails = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.loadingProductDetails
  );
  const setIsSwapItemModalOpen = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.setIsSwapItemModalOpen
  );
  const setSwapItemData = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.setSwapItemData
  );
  const isUpdatingCart = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.isUpdatingCart
  );
  const handleSingleItemAddToCart = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleSingleItemAddToCart
  );

  // Use prop quantity if provided, otherwise default to 0
  const quantity = propQuantity !== undefined ? propQuantity : 0;

  const getDisplayValue = (value: string | number | null | undefined): string => {
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      return isEmpty(trimmedValue) ? '' : trimmedValue;
    }
    if (isEmpty(value)) {
      return '';
    }
    return String(value);
  };

  const handleCheckboxKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    // Allow default checkbox behavior for Space key
    if (e.key === 'Enter' || e.key === ' ') {
      onSelect();
    }
  };

  const cardId = `product-card-${itemNum}`;
  const descriptionId = `${cardId}-description`;
  const productDetailsId = `${cardId}-details`;

  const [handleAddToCartClick, isAddToCartPending] = useGuardedAsyncCallback(
    async (e: MouseEvent<HTMLButtonElement>, product: Product) => {
      e.preventDefault();
      e.stopPropagation();
      await handleSingleItemAddToCart({
        productId: product.productId,
        catalogId: product.catalogId,
        categoryId: product.categoryId,
        quantity: quantity,
        isCompanySpecified: product.isFormulary || false,
      });
    }
  );
  const isCartActionPending = isUpdatingCart || isAddToCartPending;
  const isDisabled =
    loadingProductDetails || isFetchingProductDetails || isCartActionPending;

  const isLb = uom?.toLowerCase() === 'lb';
  const formattedPrice = intl.format(isLb ? '{0:c3}' : '{0:c2}', Number(price));

  const handleSwapItemClick = (
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setIsSwapItemModalOpen(true);
    setSwapItemData({
      pageType: 4,
      supplierId: product.supplierId,
      itemNumber: product.itemNum,
      productId: product.productId,
      catalogId: product.catalogId,
      categoryId: product.categoryId,
      quantity: quantity,
      supplierCompanyId: product.supplierCompanyId,
      price: Number(product.price),
      uom: product.uom,
    });
  };

  const isSupplierUser = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.isSupplierUser
  );

  return (
    <article
      onClick={(e) => handleViewProductDetails(e, product)}
      id={cardId}
      role="button"
      aria-label={t(
        'Product card: {{productName}}. {{status}}. Press Enter or Space to {{action}}.',
        {
          productName: description,
          status: selected ? t('Selected') : t('Not selected'),
          action: selected ? t('deselect') : t('select'),
        }
      )}
      aria-pressed={selected}
      aria-describedby={`${descriptionId} ${productDetailsId}`}
      className={clsx(
        'cursor-pointer transition-all duration-200',
        'hover:bg-[#ECF7FC] hover:text-white hover:border-[#3FA9DB]',
        'focus:outline-none',
        'relative overflow-hidden rounded-md border-[1px] border-[#C6C6C6] bg-white shadow-sm',
        'w-full max-w-[280px] h-[360px]',
        'sm:max-w-[280px] md:max-w-[290px]',
        selected && 'bg-[#ECF7FC] border-[#3FA9DB] shadow-none',
        'flex flex-col justify-between',
        isDisabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {!isExternalCatalog && !isSupplierUser ? (
        <Checkbox
          id={cardId}
          className="absolute top-2 left-2.5 z-30 w-4 h-4 cursor-pointer focus:outline-none"
          checked={selected}
          disabled={isCartActionPending}
          aria-label={t('Select {{productName}}', { productName: description })}
          onChange={(e: CheckboxChangeEvent): void => {
            onSelect();
          }}
          onKeyDown={handleCheckboxKeyDown}
          onClick={(e: MouseEvent<HTMLInputElement>): void => {
            e.stopPropagation();
          }}
        />
      ) : (
        <></>
      )}

      <div className="w-[100%]">
        <div className="relative">
          <div
            style={{
              background:
                'linear-gradient(315deg, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.2) 60%, rgba(0, 0, 0, 0.5) 80%, rgb(0, 0, 0) 100%)',
              opacity: 0.3,
            }}
            className="absolute top-0 left-0 w-full h-full"
          />
          <ProductImage
            productKey={product.productKey}
            productId={product.productId}
            supplierId={product.supplierId}
            linkUrl={product.linkUrl}
            altText={product.altText}
            type="summary"
            imageHeight="100px"
            imageWidth="100%"
            imageClassName="mb-3 h-[90px] w-full object-cover sm:h-[100px] md:h-[120px] bg-gray-200"
            reportBrokenImage
          />
        </div>
        <p
          id={descriptionId}
          className={clsx(
            'text-sm font-medium sm:text-base px-3 break-all',
            'line-clamp-2',
            'text-[#111111]',
            'h-[48px]',
            'mb-1'
          )}
          data-tooltip-id="global-tooltip"
          data-tooltip-content={getDisplayValue(description)}
        >
          {getDisplayValue(description)}
        </p>
        <div className="flex w-full flex-col px-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0 flex-1 pr-2">
              <span className={clsx('text-sm font-semibold sm:text-base', 'text-[#3F6578]')}>
                {formattedPrice}
              </span>
              {!isEmpty(uom) && (
                <span
                  className={clsx(
                    'text-xs font-normal sm:text-sm',
                    'text-[#AAAAAA]',
                    'inline-block max-w-full truncate'
                  )}
                  data-tooltip-id="global-tooltip"
                  data-tooltip-content={getDisplayValue(uom)}
                >
                  {' '}
                  / {uom}
                </span>
              )}
            </div>
            <ProductAttributeIcons
              attributes={product.attributes.filter(
                (attribute: string): boolean => attribute !== 'isImageAvailable'
              )}
            />
          </div>
        </div>
      </div>
      <div className="px-3 pb-3 sm:px-4">
        <dl id={productDetailsId} className="mb-3 grid grid-rows-3 gap-1.5 text-xs sm:text-sm">
          <div
            className="h-full w-full gap-1 grid grid-cols-[50px_1fr]"
            data-tooltip-id="global-tooltip"
            data-tooltip-content={getDisplayValue(itemNum)}
          >
            <dt
              className="font-normal text-[#AAAAAA] whitespace-nowrap truncate min-w-0"
              title={t('Item #')}
            >
              {t('Item #')}
            </dt>
            <dd className={clsx('font-normal', 'text-black', 'truncate w-full min-w-0')}>
              {getDisplayValue(itemNum)}
            </dd>
          </div>
          <div
            className="h-full w-full gap-1 grid grid-cols-[50px_1fr]"
            data-tooltip-id="global-tooltip"
            data-tooltip-content={getDisplayValue(brandName)}
          >
            <dt className="font-normal text-[#AAAAAA] whitespace-nowrap truncate min-w-0">
              {t('Brand')}
            </dt>
            <dd className={clsx('font-normal', 'text-black', 'truncate w-full min-w-0')}>
              {getDisplayValue(brandName)}
            </dd>
          </div>
          {isOfflineSupplier && (
            <div
              className="h-full w-full gap-1 grid grid-cols-[50px_1fr]"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={getDisplayValue(companyName)}
            >
              <dt
                className="font-normal text-[#AAAAAA] whitespace-nowrap truncate min-w-0"
                title={t('Supplier')}
              >
                {t('Supplier')}
              </dt>
              <dd className={clsx('font-normal line-clamp-1', 'text-black')}>
                {getDisplayValue(companyName)}
              </dd>
            </div>
          )}
        </dl>
        <div className="flex w-full min-w-0 flex-row flex-nowrap items-center gap-1.5">
          {/* Qty label */}
          <span className="shrink-0 text-xs font-normal text-[#AAAAAA] sm:text-sm">{t('Qty')}</span>

          {/* Stepper — flex-1 so it absorbs leftover space */}
          <NumberStepper
            data-tooltip-id="global-tooltip"
            data-tooltip-content={
              isExternalCatalog ? t('Click Open External Catalog button to order.') : ''
            }
            disabled={(isExternalCatalog || isSupplierUser || isCartActionPending) ?? false}
            value={quantity}
            onChange={(e: SyntheticEvent<Element, Event>, value: number): void => {
              e.stopPropagation();
              if (onQuantityChange) {
                onQuantityChange(value);
              }
            }}
            onClick={(e: MouseEvent<HTMLDivElement>): void => {
              e.stopPropagation();
            }}
            onMouseDown={(e: MouseEvent<HTMLDivElement>): void => {
              e.stopPropagation();
            }}
            className="h-[25px] min-w-[72px] flex-1"
          />

          {/* Cart button */}
          {!isExternalCatalog && !isSupplierUser && (
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
              className="max-h-[180px]"
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper type="Information" contents={[t('Quantity cannot be 0')]} />
                </div>
              }
              show={quantity === 0}
            >
              <div className="shrink-0">
                <Button
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCartClick(e, product);
                  }}
                  size="sm"
                  disabled={quantity === 0 || isCartActionPending}
                  loading={isCartActionPending}
                  className="!m-0 h-[25px] w-[30px] min-w-[30px] justify-center px-0"
                >
                  <AddToCartIcon
                    height="16px"
                    width="16px"
                    className={clsx({ 'cursor-not-allowed': quantity === 0 })}
                  />
                </Button>
              </div>
            </PopUp>
          )}

          {/* Contracted-alternatives icon */}
          {product.isNoItem && !isSupplierUser && (
            <div
              data-tooltip-id="global-tooltip"
              data-tooltip-content={t('Check Contracted Alternatives')}
              onClick={handleSwapItemClick}
              className="flex shrink-0 cursor-pointer items-center"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  handleSwapItemClick(event);
                }
              }}
            >
              <NoItemIcon />
            </div>
          )}

          {/* View Info — shrink-0 + w-fit keeps the button exactly content-sized so no
              phantom left-space appears between the preceding icon and the visible text */}
          <button
            disabled={isDisabled}
            type="button"
            onClick={(e) => handleViewProductDetails(e, product)}
            className="flex w-fit shrink-0 flex-col items-start leading-[1.1] text-xs font-medium text-[#3FA9DB] hover:underline focus:outline-2 focus:outline-black rounded sm:text-sm"
            aria-label={t('View more information about {{productName}}', {
              productName: description,
            })}
          >
            <span>{t('View')}</span>
            <span>{t('Info')}</span>
          </button>
        </div>
      </div>
    </article>
  );
};
